import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Paper,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  AttachMoney as PriceIcon,
  TrendingUp as TrendingUpIcon,
  Chat as ChatIcon,
  Assessment as AssessmentIcon,
  Factory as FactoryIcon,
  Inventory2 as Inventory2Icon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { productService, API_ROOT_URL } from '../services/api';
import { metalPricesService } from '../services/metalPrices';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Imagen del hero: primero variable de entorno, luego archivo local en public, y si no existe podrás cargar la tuya
  const heroImageUrl = (import.meta?.env?.VITE_HERO_IMAGE_URL) || '/images/metal-hero.jpg';

  // Precios de mercado (estado) con default - Metales expandidos
  const [marketPrices, setMarketPrices] = useState({
    aluminio: { price: 45.80, change: '-1.2%', trend: 'down' },
    cobre: { price: 89.20, change: '+0.8%', trend: 'up' },
    laton: { price: 67.40, change: '+1.5%', trend: 'up' },
    niquel: { price: 156.80, change: '+2.1%', trend: 'up' },
    plomo: { price: 23.45, change: '-0.5%', trend: 'down' },
    estano: { price: 312.60, change: '+1.8%', trend: 'up' }
  });

  const features = [
    {
      icon: <Inventory2Icon sx={{ fontSize: 40, color: '#0ea5e9' }} />,
      title: 'Catálogo de Productos',
      description: 'Explora lámina, bobina, barra y tubo en distintas especificaciones.'
    },
    {
      icon: <FactoryIcon sx={{ fontSize: 40, color: '#22c55e' }} />,
      title: 'Proveedores Verificados',
      description: 'Conecta con empresas confiables y perfiles empresariales.'
    },
    {
      icon: <ChatIcon sx={{ fontSize: 40, color: '#f43f5e' }} />,
      title: 'Contacto Directo',
      description: 'Chatea con los proveedores para concretar tus compras.'
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: '#eab308' }} />,
      title: 'Precios y Disponibilidad',
      description: 'Consulta precios de referencia y disponibilidad actualizada.'
    }
  ];

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);
        const response = await productService.getProducts({ ordering: '-created_at' });
        const products = response.data.results ? response.data.results : response.data;
        setFeaturedProducts(products.slice(0, 8)); // Mostrar 8 productos
      } catch (err) {
        console.error('Error fetching products:', err);
        setFeaturedProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedProducts();
    
    // Cargar precios desde backend usando el nuevo servicio
    const fetchPrices = async () => {
      try {
        console.log('🔄 Obteniendo precios de metales...');
        const prices = await metalPricesService.getFrontendPrices();
        console.log('✅ Precios obtenidos:', prices);
        setMarketPrices(prices);
      } catch (e) {
        console.error('❌ Error obteniendo precios:', e);
        // Mantener defaults en caso de error
      }
    };
    fetchPrices();
  }, []);

  // Función para generar nombre del producto
  const generarNombreProducto = (producto) => {
    if (!producto || !producto.mercado || !producto.metal || !producto.presentacion) return 'Producto sin título';
    let extra = '';
    if (producto.especificaciones?.espesor) extra = ` - ${producto.especificaciones.espesor}mm`;
    if (producto.especificaciones?.diametro) extra = ` - Ø${producto.especificaciones.diametro}mm`;
    return `${producto.mercado} - ${producto.metal} - ${producto.presentacion}${extra}`;
  };

  return (
    <Box>
      {/* Hero industrial */}
      <Box
        className="hero-section"
        sx={{
          position: 'relative',
          backgroundImage: `linear-gradient(rgba(15,23,42,0.75), rgba(17,24,39,0.85)), url(${heroImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: 6,
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <Container maxWidth="lg" className="hero-content">
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12}>
              <Typography variant="h2" component="h1" gutterBottom className="hero-title" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                Comercio de Metales
              </Typography>
              <Typography variant="h6" component="h2" gutterBottom className="hero-subtitle" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                Conecta con proveedores verificados y negocia directo.
              </Typography>
              <Box className="hero-buttons" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/products"
                  className="custom-button"
                  sx={{ mr: 2, backgroundColor: '#f43f5e', '&:hover': { backgroundColor: '#e11d48' } }}
                >
                  Explorar Catálogo
                </Button>
                {/* Se elimina botón secundario para evitar llamadas a acciones no funcionales por ahora */}
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card className="price-card" sx={{ backgroundColor: '#0f172a', color: '#ffffff', border: '1px solid rgba(255,255,255,0.18)', maxWidth: 800, width: '100%' }}>
                <CardContent sx={{ textAlign: 'center', color: '#ffffff' }}>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)' }}>Precios de referencia (MXN/kg)</Typography>
                  <Grid container spacing={1.5} sx={{ mt: 1 }}>
                    {Object.entries(marketPrices).map(([metal, data]) => (
                      <Grid item xs={6} sm={3} key={metal}>
                        <Box textAlign="center" className="price-item" sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize', color: 'rgba(255,255,255,0.9)', fontSize: '0.75rem', fontWeight: 500 }}>
                            {metal}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff', fontSize: '1rem', my: 0.5 }}>
                            {data.price !== null ? `$${data.price}` : 'N/A'}
                          </Typography>
                          <Chip
                            label={data.change}
                            size="small"
                            color={data.trend === 'up' ? 'success' : 'error'}
                            icon={<TrendingUpIcon />}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          <Box sx={{ mt: 0.5 }}>
                            <a 
                              href={data.source || 'https://finance.yahoo.com'} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              style={{ color: '#93c5fd', fontSize: 11 }}
                            >
                              Ver fuente
                            </a>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Productos destacados como grilla */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4">Productos Destacados</Typography>
          <TextField
            size="small"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SearchIcon /></InputAdornment>
            ) }}
            sx={{ width: 360, maxWidth: '100%' }}
          />
        </Box>
        <Grid container spacing={3} justifyContent="center">
          {loading ? (
            <Grid item xs={12}><Paper sx={{ p: 2 }}>Cargando productos...</Paper></Grid>
          ) : (
            (featuredProducts || [])
              .filter(product => {
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                const name = generarNombreProducto(product).toLowerCase();
                const description = (product?.description || '').toLowerCase();
                const metal = (product?.metal || '').toLowerCase();
                const mercado = (product?.mercado || '').toLowerCase();
                return name.includes(searchLower) || description.includes(searchLower) || metal.includes(searchLower) || mercado.includes(searchLower);
              })
              .slice(0, 8)
              .map((product) => (
                <Grid item xs={12} sm={6} md={3} key={product.id}>
                  <Card component={RouterLink} to={`/products/${product.id}`} className="product-card" sx={{ textDecoration: 'none', ':hover': { boxShadow: 6 } }}>
                    {product?.images?.length ? (
                      <CardMedia 
                        component="img" 
                        height="160" 
                        image={product.images[0].image.startsWith('http') ? 
                          product.images[0].image.replace(/^http:/, 'https:') : 
                          `${API_ROOT_URL}${product.images[0].image}`} 
                        alt={product.title || 'producto'} 
                      />
                    ) : (
                      <Box sx={{ height: 160, background: 'linear-gradient(135deg, #0ea5e9 0%, #22d3ee 100%)' }} />
                    )}
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom noWrap>{generarNombreProducto(product)}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Chip size="small" label={product?.mercado || 'N/A'} />
                        <Typography variant="body2" color="primary">
                          {product?.contact_for_price ? 'Cotizar' : `$${product?.price || 'N/A'}`}
                        </Typography>
                      </Box>
                      {/* Información del proveedor */}
                      {product?.supplier && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                          {product.supplier.business_profile?.company_name || product.supplier.username}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))
          )}
        </Grid>
        <Box textAlign="center" sx={{ mt: 4 }}>
          <Button variant="contained" size="large" component={RouterLink} to="/products" className="custom-button" sx={{ backgroundColor: '#0ea5e9', '&:hover': { backgroundColor: '#0284c7' } }}>Ver todo</Button>
        </Box>
      </Container>

      {/* Servicios / capacidades */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={3} justifyContent="center">
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="feature-card" sx={{ height: '100%', border: '1px solid #e5e7eb' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box className="feature-icon" sx={{ mb: 1 }}>{feature.icon}</Box>
                  <Typography variant="h6" gutterBottom>{feature.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{feature.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Banda de contacto */}
      <Paper className="contact-band" sx={{ background: 'linear-gradient(180deg, #0f172a 0%, #111827 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
            ¿Listo para empezar?
          </Typography>
          <Typography variant="body1" textAlign="center" paragraph sx={{ opacity: 0.85 }}>
            Hablemos de tus necesidades de metal. Respuesta rápida y soporte experto.
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button variant="contained" size="large" component={RouterLink} to="/register" className="custom-button" sx={{ backgroundColor: '#f43f5e', '&:hover': { backgroundColor: '#e11d48' } }}>Crear Cuenta</Button>
          </Box>
        </Container>
      </Paper>
    </Box>
  );
};

export default Home; 